import { type FragmentType, graphql, useFragment } from "@repo/graphql-types";
import { Money2, Wallet } from "@repo/icons";
import { checkIfAppointmentHasOccurred, formatCurrency } from "@repo/lib";
import { Link } from "@tanstack/react-router";
import { CpsButtonText, CpsLinedData } from "corpus";
import { PaymentStatusBadge } from "@/components/payment-status-badge";
import { getPaymentsPaidAndPendingAmounts } from "@/lib/payments";

export const AppointmentDrawerOnlinePaymentButtonFragment = graphql(/* GraphQL */ `
  fragment AppointmentDrawerOnlinePaymentButtonFragment on agendamentos {
    codAgendamento
    data
    horaInicio
    pago
    pagar
    valor
    onlinePayments(where: { status: { _neq: "canceled" } }) {
      codAgendamento
      amount
      status
    }
    agendamentosVinculados: AgendamentosVinculados(
      where: { codAgendamentoTipoVinculo: { _eq: 2 } }
    ) {
      onlinePayments(where: { status: { _neq: "canceled" } }) {
        codAgendamento
        amount
        status
      }
    }
  }
`);

interface AppointmentDrawerOnlinePaymentButtonProps {
  fragmentData: FragmentType<typeof AppointmentDrawerOnlinePaymentButtonFragment>;
}

export const AppointmentDrawerPaymentBadge = ({
  status,
  amount,
}: {
  status: "paid" | "pending";
  amount: number;
}): JSX.Element => (
  <div className="flex justify-start w-full">
    <div className="flex flex-row mr-2">
      <p>R${formatCurrency(amount).replace("R$", "")}</p>
    </div>
    <PaymentStatusBadge status={status} />
  </div>
);

export const AppointmentDrawerOnlinePaymentInformation = ({
  fragmentData,
}: AppointmentDrawerOnlinePaymentButtonProps): JSX.Element => {
  const fragmentResult = useFragment(
    AppointmentDrawerOnlinePaymentButtonFragment,
    fragmentData,
  );

  const {
    codAgendamento,
    data,
    horaInicio,
    onlinePayments,
    valor,
    pago,
    pagar,
    agendamentosVinculados,
  } = fragmentResult;

  const relatedAppointmentsOnlinePayments = agendamentosVinculados.flatMap(
    (item) => item.onlinePayments,
  );

  const isAppointmentAlreadyOccurred = checkIfAppointmentHasOccurred(data, horaInicio);

  const buttonLabel = isAppointmentAlreadyOccurred
    ? "Criar pagamento"
    : "Criar pagamento antecipado";

  const hasPayment =
    pagar || onlinePayments.length > 0 || relatedAppointmentsOnlinePayments.length > 0;

  const buildPaymentData = (): JSX.Element | null => {
    if (!hasPayment) return null;

    const { totalPaid, totalPending } = getPaymentsPaidAndPendingAmounts(
      pagar,
      valor,
      pago,
      onlinePayments,
      relatedAppointmentsOnlinePayments,
    );

    return (
      <div className="flex flex-col gap-2 justify-start">
        {totalPaid > 0 && (
          <CpsLinedData Icon={Money2}>
            <AppointmentDrawerPaymentBadge status="paid" amount={totalPaid} />
          </CpsLinedData>
        )}
        {totalPending > 0 && (
          <CpsLinedData hideIcon={totalPaid !== 0} Icon={Money2}>
            <AppointmentDrawerPaymentBadge status="pending" amount={totalPending} />
          </CpsLinedData>
        )}
      </div>
    );
  };

  return (
    <div>
      <Link
        to="/settings/payments/create"
        preload={false}
        search={{ appointmentId: codAgendamento, origin: "Drawer de agendamento" }}
      >
        <CpsButtonText
          className="flex flex-row gap-1 p-0 mb-2"
          Icon={Wallet}
          color="secondary"
        >
          {buttonLabel}
        </CpsButtonText>
      </Link>
      {buildPaymentData()}
    </div>
  );
};
